import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { InlineMessageProps } from '@/components/inline-message/InlineMessage'
import { Modal, ModalContainer, ModalFooterWithActions, ModalHeader, TabbedModal } from '@/components/modal'
import { useAppDispatch } from '@/store/store-hooks'
import { BrowseMatterFiles } from '@/user_files/components/BrowseMatterFiles'
import { useBrowseFiles } from '@/user_files/components/useBrowseFiles'
import { useRecursiveFolderSelection } from '@/user_files/components/useRecursiveFolderSelection'
import { kGetFeatureFlagStatus, kFeatureFlags } from '@/constants/constants-feature-flags'
import type { UserFileRead } from '@/uppy-file-upload/schemas'
import { useExternalFilesTab } from '@/user_files/external-storage/hooks/useExternalFilesTab'
import { filterLocalStorageItems, isLocalStorageItem, isVisibleExternalStorageItem } from '@/user_files/external-storage/utils/external-storage-items'
import { isSelectableProcessingStatus } from '@/user_files/utils/processing-status'
import { uniquePreserveOrder } from '@/util/array-utils'

export const ADD_FILES_MODAL_TAB = {
  MATTER_FILES: 'matter-files',
  EXTERNAL_FILES: 'external-files',
} as const

const ADD_FILES_TAB_MATTER_FILES = ADD_FILES_MODAL_TAB.MATTER_FILES
const ADD_FILES_TAB_TEMPLATES = 'templates'
const ADD_FILES_TAB_EXTERNAL_FILES = ADD_FILES_MODAL_TAB.EXTERNAL_FILES
const EMPTY_SELECTED_FILE_IDS: string[] = []
export type AddFilesModalInitialTab = (typeof ADD_FILES_MODAL_TAB)[keyof typeof ADD_FILES_MODAL_TAB]

export type AddFilesModalProps = {
  /** Whether the modal is open. */
  open: boolean
  /** Called when the modal is closed. */
  onClose: () => void
  /** Matter whose files are listed (conversation matter from the parent; not switchable inside this modal). */
  matterId: string
  /** Called with the selected file ids when the user confirms ("Done"). The caller owns what happens next. */
  onFilesSelected: (userFileIds: string[]) => void
  /** Optional file ids to preselect when the modal opens. */
  initialSelectedFileIds?: string[]
  /** 'single' replaces the prior pick on each toggle; 'multiple' adds/removes. Defaults to 'multiple'. */
  selectionMode?: 'single' | 'multiple'
  /** Modal title. Defaults to 'Add files'. */
  title?: string
  /** Optional. Folder to open the browse UI on; otherwise browsing starts at the matter root. If set, must belong to `matterId`; invalid ids fall back to root. */
  initialFolderId?: string
  /** Optional status message above the footer actions. */
  inlineMessage?: InlineMessageProps
  /** Optional file-level selection rule. Defaults to allowing every loaded file. */
  isFileSelectable?: (file: UserFileRead) => boolean
  /** Optional tooltip shown for files rejected by `isFileSelectable`. */
  fileNotSelectableMessage?: string
  /** Optional tab to show first when the modal opens. */
  initialTab?: AddFilesModalInitialTab
}

export function AddFilesModal({
  open,
  onClose,
  matterId,
  onFilesSelected,
  initialSelectedFileIds = EMPTY_SELECTED_FILE_IDS,
  selectionMode = 'multiple',
  title = 'Add files',
  initialFolderId,
  inlineMessage,
  isFileSelectable,
  fileNotSelectableMessage,
  initialTab = ADD_FILES_TAB_MATTER_FILES,
}: AddFilesModalProps) {
  const dispatch = useAppDispatch()
  const singleSelect = selectionMode === 'single'

  const [selectedFileIds, setSelectedFileIds] = useState<string[]>(initialSelectedFileIds)
  const [activeTabId, setActiveTabId] = useState<string>(ADD_FILES_TAB_MATTER_FILES)
  // Anchor for Shift-click range selection within the current visible file list.
  const lastSelectedFileIdRef = useRef<string | null>(null)

  const matterFilesBrowse = useBrowseFiles({
    matterId,
    open,
    initialFolderId,
  })

  const externalFilesTab = useExternalFilesTab({
    matterId,
    open,
    active: activeTabId === ADD_FILES_TAB_EXTERNAL_FILES,
    fallbackBreadcrumbItems: matterFilesBrowse.breadcrumbItems,
  })

  const showTabbedLayout = externalFilesTab.showExternalTab
  const filterRecursiveSelectableFiles = useCallback(
    (file: UserFileRead) =>
      activeTabId === ADD_FILES_TAB_EXTERNAL_FILES ? isVisibleExternalStorageItem(file, externalFilesTab.syncedProvider) : isLocalStorageItem(file),
    [activeTabId, externalFilesTab.syncedProvider]
  )

  const {
    clearRecursiveFolderSelection,
    getFolderSelectionState,
    getFolderSelectionTooltipMessage,
    hasRecursiveFolderSelectionPending,
    recursiveFolderSelectionMessage,
    toggleFolderSelection,
  } = useRecursiveFolderSelection({
    dispatch,
    filterSelectableFiles: filterRecursiveSelectableFiles,
    isFileSelectable,
    selectedFileIds,
    setSelectedFileIds,
  })

  // Reset selection and tab when the modal is opened.
  useEffect(() => {
    if (!open) return
    setSelectedFileIds(initialSelectedFileIds)
    setActiveTabId(initialTab)
    lastSelectedFileIdRef.current = null
    clearRecursiveFolderSelection()
  }, [open, matterId, initialSelectedFileIds, initialTab, clearRecursiveFolderSelection])

  useEffect(() => {
    if (activeTabId === ADD_FILES_TAB_EXTERNAL_FILES && !externalFilesTab.externalSyncStatus.loading && !showTabbedLayout) {
      setActiveTabId(ADD_FILES_TAB_MATTER_FILES)
    }
  }, [activeTabId, externalFilesTab.externalSyncStatus.loading, showTabbedLayout])

  const localFolders = useMemo(() => filterLocalStorageItems(matterFilesBrowse.folders), [matterFilesBrowse.folders])
  const localFiles = useMemo(() => filterLocalStorageItems(matterFilesBrowse.files), [matterFilesBrowse.files])
  const visibleFiles = activeTabId === ADD_FILES_TAB_EXTERNAL_FILES ? externalFilesTab.browseProps.files : localFiles

  const isFileAllowedForSelection = (file: UserFileRead) => isSelectableProcessingStatus(file.processing_status) && (isFileSelectable?.(file) ?? true)

  const handleToggleFile = (file: UserFileRead, options?: { shiftKey?: boolean }) => {
    const fileId = file.id
    const canSelectFile = isFileAllowedForSelection(file)
    const fileWasSelected = selectedFileIds.includes(fileId)
    const anchorFileId = lastSelectedFileIdRef.current
    const anchorIndex = anchorFileId ? visibleFiles.findIndex((visibleFile) => visibleFile.id === anchorFileId) : -1
    const fileIndex = visibleFiles.findIndex((visibleFile) => visibleFile.id === fileId)

    // Shift-click only adds the visible range; it does not remove existing selections.
    if (!singleSelect && options?.shiftKey && canSelectFile && anchorIndex >= 0 && fileIndex >= 0 && anchorIndex !== fileIndex) {
      const startIndex = Math.min(anchorIndex, fileIndex)
      const endIndex = Math.max(anchorIndex, fileIndex)
      const rangeFileIds = visibleFiles
        .slice(startIndex, endIndex + 1)
        .filter(isFileAllowedForSelection)
        .map((visibleFile) => visibleFile.id)

      setSelectedFileIds((prev) => uniquePreserveOrder([...prev, ...rangeFileIds]))
      lastSelectedFileIdRef.current = fileId
      return
    }

    setSelectedFileIds((prev) => {
      const isSelected = prev.includes(fileId)
      if (!isSelected && !canSelectFile) return prev
      // Single-select replaces the prior choice on each toggle; multi-select adds/removes.
      if (singleSelect) return isSelected ? [] : [fileId]
      return isSelected ? prev.filter((id) => id !== fileId) : [...prev, fileId]
    })

    if (!fileWasSelected && canSelectFile) {
      lastSelectedFileIdRef.current = fileId
    }
  }

  const handleClose = () => {
    clearRecursiveFolderSelection()
    onClose()
  }

  const handleApply = () => {
    onFilesSelected(selectedFileIds)
    handleClose()
  }

  const handleClearSelection = () => {
    setSelectedFileIds([])
    lastSelectedFileIdRef.current = null
    clearRecursiveFolderSelection()
  }

  const footerInlineMessage: InlineMessageProps | undefined = recursiveFolderSelectionMessage
    ? {
        variant: recursiveFolderSelectionMessage.variant,
        children: recursiveFolderSelectionMessage.text,
      }
    : inlineMessage
  const isSelectionTab = !showTabbedLayout || activeTabId === ADD_FILES_TAB_MATTER_FILES || activeTabId === ADD_FILES_TAB_EXTERNAL_FILES
  const footer = (
    <ModalFooterWithActions
      inlineMessage={footerInlineMessage}
      tertiaryText={`${selectedFileIds.length} ${selectedFileIds.length === 1 ? 'file' : 'files'} selected`}
      secondaryAction={{
        label: 'Clear',
        onClick: handleClearSelection,
        disabled: !isSelectionTab,
      }}
      primaryAction={{
        label: 'Done',
        onClick: handleApply,
        // Done applies the current selection, including an empty one — so deselecting (or Clear)
        // and confirming clears the attachment instead of trapping the user. Disabled only on the
        // non-files tabs, or while a recursive folder selection is still resolving.
        disabled: !isSelectionTab || hasRecursiveFolderSelectionPending,
      }}
    />
  )
  const matterFilesPanel = (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <BrowseMatterFiles
        breadcrumbItems={matterFilesBrowse.breadcrumbItems}
        files={localFiles}
        folders={localFolders}
        blockingError={matterFilesBrowse.error}
        loading={matterFilesBrowse.loading}
        // Folder select-all only applies to multi-select; the single-select picker (writing
        // sample) omits it so selecting a folder can't pull in more than one file.
        getFolderSelectionState={singleSelect ? undefined : getFolderSelectionState}
        getFolderSelectionTooltipMessage={singleSelect ? undefined : getFolderSelectionTooltipMessage}
        onToggleFolderSelection={singleSelect ? undefined : toggleFolderSelection}
        onNavigateToFolder={matterFilesBrowse.onNavigateToFolder}
        onRetry={matterFilesBrowse.retry}
        onToggleFile={handleToggleFile}
        selectedFileIds={selectedFileIds}
        isFileSelectable={isFileSelectable}
        fileNotSelectableMessage={fileNotSelectableMessage}
      />
    </div>
  )

  return (
    <Modal
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleClose()
      }}
    >
      {showTabbedLayout ? (
        <TabbedModal
          className="bg-white"
          title={title}
          width="lg"
          height="md"
          showCloseButton
          activeTabId={activeTabId}
          onTabChange={setActiveTabId}
          footer={footer}
          tabs={[
            {
              id: ADD_FILES_TAB_MATTER_FILES,
              label: 'Matter Files',
              panel: matterFilesPanel,
            },
            ...(kGetFeatureFlagStatus(kFeatureFlags.TEMPLATES_TAB)
              ? [
                  {
                    id: ADD_FILES_TAB_TEMPLATES,
                    label: 'Templates',
                    panel: (
                      <div className="flex min-h-[240px] flex-col items-center justify-center px-6 py-10 text-center text-sm text-brand-neutral-500">
                        <p>Templates are not available yet.</p>
                      </div>
                    ),
                  },
                ]
              : []),
            {
              id: ADD_FILES_TAB_EXTERNAL_FILES,
              label: externalFilesTab.syncedProviderLabel ?? '',
              panel: (
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                  <BrowseMatterFiles
                    {...externalFilesTab.browseProps}
                    getFolderSelectionState={singleSelect ? undefined : getFolderSelectionState}
                    getFolderSelectionTooltipMessage={singleSelect ? undefined : getFolderSelectionTooltipMessage}
                    onToggleFolderSelection={singleSelect ? undefined : toggleFolderSelection}
                    onToggleFile={handleToggleFile}
                    selectedFileIds={selectedFileIds}
                    isFileSelectable={isFileSelectable}
                    fileNotSelectableMessage={fileNotSelectableMessage}
                  />
                </div>
              ),
            },
          ]}
        />
      ) : (
        <ModalContainer className="bg-white" width="lg" height="md" data-ds-component="AddFilesModal">
          <div className="flex min-h-0 min-w-0 flex-1 flex-col pt-6">
            <div className="shrink-0 px-6 pb-4">
              <ModalHeader title={title} showCloseButton />
            </div>
            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden border-y border-brand-neutral-200">{matterFilesPanel}</div>
            <div className="mt-auto shrink-0 px-6 py-4">{footer}</div>
          </div>
        </ModalContainer>
      )}
    </Modal>
  )
}
